import { useCallback, useRef, useEffect } from 'react';
import { debounce } from 'lodash';

export interface UseDebouncedTooltipProps {
  showTooltip: (event: React.MouseEvent, conversationId: string, customAnchorEl?: HTMLElement) => void;
  hideTooltip: () => void;
  isMobile: boolean;
  conversationId: string | undefined;
}

/**
 * A custom hook that manages debounced tooltip functionality for conversation components
 * 
 * @param showTooltip Function to show the tooltip
 * @param hideTooltip Function to hide the tooltip
 * @param isMobile Boolean indicating if the device is mobile
 * @param conversationId The ID of the conversation
 * @returns Object containing event handlers for mouse enter and leave
 */
export default function useDebouncedTooltip({
  showTooltip,
  hideTooltip,
  isMobile,
  conversationId
}: UseDebouncedTooltipProps) {
  // Create stored ref for the conversation ID to prevent recreation of debounced functions
  const conversationIdRef = useRef(conversationId);

  // Keep the ref updated
  useEffect(() => {
    conversationIdRef.current = conversationId;
  }, [conversationId]);

  // Create debounced show tooltip function with 700ms delay
  const debouncedShowTooltip = useCallback(
    debounce((elementTarget, anchorElement) => {
      // Double-check we're still not on mobile when the debounce resolves
      if (!isMobile && conversationIdRef.current) {
        // Ensure the target element is still in the document
        if (elementTarget && elementTarget.isConnected && document.body.contains(elementTarget)) {
          // Check the custom anchor too if provided
          if (anchorElement && anchorElement !== elementTarget) {
            if (!anchorElement.isConnected || !document.body.contains(anchorElement)) {
              // Use elementTarget as fallback if customAnchor is invalid
              anchorElement = elementTarget;
            }
          }

          showTooltip(
            { currentTarget: elementTarget } as React.MouseEvent<HTMLElement>,
            conversationIdRef.current,
            anchorElement
          );
        }
      }
    }, 200),
    [isMobile, showTooltip]
  );

  // Create debounced hide tooltip function with 700ms delay
  const debouncedHideTooltip = useCallback(
    debounce(() => {
      // Double-check we're still not on mobile when the debounce resolves
      if (!isMobile) {
        hideTooltip();
      }
    }, 300),
    [isMobile, hideTooltip]
  );

  // Handle mouse enter with proper event capture
  const handleMouseEnter = useCallback((e: React.MouseEvent, customAnchorEl?: HTMLElement) => {
    if (!conversationIdRef.current) return;

    // Cancel any pending hide
    debouncedHideTooltip.cancel();

    // Store the target element
    const currentTarget = e.currentTarget;

    // Make sure target is valid
    if (currentTarget && 'isConnected' in currentTarget && currentTarget.isConnected) {
      // If custom anchor is provided, also check its validity
      let anchor = customAnchorEl || currentTarget;
      if (customAnchorEl && (!customAnchorEl.isConnected || !document.body.contains(customAnchorEl))) {
        anchor = currentTarget;
      }

      // Use custom anchor if provided, otherwise use the event target
      debouncedShowTooltip(currentTarget, anchor);
    }
  }, [debouncedShowTooltip, debouncedHideTooltip]);

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    // Cancel any pending show
    debouncedShowTooltip.cancel();
    // Schedule a hide
    debouncedHideTooltip();
  }, [debouncedShowTooltip, debouncedHideTooltip]);

  // Clean up function
  useEffect(() => {
    return () => {
      debouncedShowTooltip.cancel();
      debouncedHideTooltip.cancel();
    };
  }, [debouncedShowTooltip, debouncedHideTooltip]);

  return {
    handleMouseEnter,
    handleMouseLeave
  };
} 
